/**
 * Client-side chat history cleanup aligned with Sunshine Conversations clearing guidance:
 * https://docs.smooch.io/guide/clearing-conversations/
 *
 */

import { ErrorManagement } from '@printdeal/error-management/react';
import type { ZendeskChatClearReason } from './chatTypes';
import { logZendeskChat } from './zendesk-chat-logger';
import { clearZendeskConversationMessagesOnServer } from './zendesk-chat-server';

const LAST_ACTIVITY_KEY = 'zendeskChatLastActivityAt';
const CONVERSATION_ID_KEY = 'zendeskConversationId';

// Inactivity window after which the chat history is cleared. Configurable per environment
// (e.g. a short value for testing) via GATSBY_ZENDESK_CHAT_RETENTION_MINUTES, defaulting to 15 min.

const retentionMinutes = Number(
  process.env.GATSBY_ZENDESK_CHAT_RETENTION_PERIOD ?? '15',
);

export const CHAT_HISTORY_RETENTION_MS = retentionMinutes * 60 * 1000;

// Throttle storage writes so high-frequency activity events (mousemove/scroll) stay cheap.
const ACTIVITY_WRITE_THROTTLE_MS = 10 * 1000;

// Pointer events unify mouse, touch and pen input, so they cover what the previous
// mouse*/touch* list did while keeping the set smaller.
const ACTIVITY_EVENTS = [
  'pointermove',
  'pointerdown',
  'keydown',
  'wheel',
  'scroll',
] as const;

interface ZendeskConversationEvent {
  payload: {
    conversation: {
      id: string;
    };
  };
}

const isBrowser = Boolean(
  typeof window !== 'undefined'
  && window.document
  && window.document.createElement,
);

let listenersRegistered = false;
let lastActivityWriteAt = 0;
let activityIntervalId: number | undefined;
// Controls the activity/visibility listeners as a group, so teardown removes them all via abort()
// without stopActivityTracking needing to reference the individual handlers (which would create a
// definition cycle with clearZendeskChat).
let activityAbortController: AbortController | undefined;

const isMessengerAvailable = (): boolean =>
  isBrowser && typeof window.zE === 'function';

// Direct localStorage access (no cookie sync): this is client-only session metadata that must
// never be pushed into cookies, per the "avoid reading/writing cookies from JavaScript" guideline.
const getStorageItem = (key: string): string | null =>
  (isBrowser ? window.localStorage.getItem(key) : null);
const setStorageItem = (key: string, value: string): void => {
  if (isBrowser) {
    window.localStorage.setItem(key, value);
  }
};
const removeStorageItem = (key: string): void => {
  if (isBrowser) {
    window.localStorage.removeItem(key);
  }
};

const touchActivity = (): void => {
  const now = Date.now();

  if (now - lastActivityWriteAt < ACTIVITY_WRITE_THROTTLE_MS) {
    return;
  }

  lastActivityWriteAt = now;
  setStorageItem(LAST_ACTIVITY_KEY, now.toString());
};

const isHistoryExpired = (): boolean => {
  const lastActivity = getStorageItem(LAST_ACTIVITY_KEY);

  if (!lastActivity) {
    return false;
  }

  const idleMs = Date.now() - Number(lastActivity);
  return idleMs >= CHAT_HISTORY_RETENTION_MS;
};

const getConversationId = (): string | null =>
  getStorageItem(CONVERSATION_ID_KEY);
const setConversationId = (conversationId: string): void => {
  setStorageItem(CONVERSATION_ID_KEY, conversationId);
};

const clearTrackingData = (): void => {
  removeStorageItem(LAST_ACTIVITY_KEY);
  removeStorageItem(CONVERSATION_ID_KEY);
};

// Detaches every activity/visibility listener at once via the shared AbortController. Kept free of
// references to the individual handlers so it can be defined before them and avoid a cycle.
const stopActivityTracking = (): void => {
  activityAbortController?.abort();
  activityAbortController = undefined;
};

// Tears down all listeners and the inactivity interval registered by registerZendeskChatListeners,
// preventing memory leaks when the chat session ends.
const unregisterZendeskChatListeners = (): void => {
  stopActivityTracking();

  if (activityIntervalId !== undefined) {
    window.clearInterval(activityIntervalId);
    activityIntervalId = undefined;
  }

  listenersRegistered = false;
};

export const clearZendeskChat = async ({
  logoutUser = false,
  reason,
}: {
  logoutUser?: boolean;
  reason: ZendeskChatClearReason;
}): Promise<void> => {
  const conversationId = getConversationId();

  try {
    if (conversationId) {
      await clearZendeskConversationMessagesOnServer(
        conversationId,
      );
    }

    if (isMessengerAvailable()) {
      if (logoutUser) {
        window.zE?.('messenger', 'logoutUser');
      }
      window.zE?.('messenger', 'resetWidget');
    }

    unregisterZendeskChatListeners();
    clearTrackingData();

    logZendeskChat('Chat cleared', {
      reason,
      conversationId,
    });
  } catch (error) {
    ErrorManagement.exception({
      exception: error,
      extra: {
        reason,
        conversationId,
        logoutUser,
      },
      tags: {
        fileLocation: 'zendesk-chat-session.ts',
        featureFunction: 'clearZendeskChat',
      },
      fingerprint: ['clear-zendesk-chat-failed'],
    });
  }
};

export const clearZendeskChatOnLogout = (): Promise<void> =>
  clearZendeskChat({
    logoutUser: true,
    reason: 'logout',
  });

// When the tab becomes visible again we re-check expiry immediately, because background tabs
// throttle the interval timer and the retention window may have elapsed while we were hidden.
const handleVisibilityChange = (): void => {
  if (document.visibilityState !== 'visible' || !isHistoryExpired()) {
    return;
  }
  clearZendeskChat({ reason: 'retention_expired' });
};

const startActivityTracking = (): void => {
  activityAbortController = new AbortController();
  const { signal } = activityAbortController;

  ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, touchActivity, { passive: true, signal });
  });
  document.addEventListener('visibilitychange', handleVisibilityChange, { signal });
  touchActivity();
};

export const registerZendeskChatListeners = async (): Promise<void> => {
  if (!isMessengerAvailable() || listenersRegistered) {
    return;
  }

  listenersRegistered = true;

  // On load, clear immediately if the previous visit went inactive past the retention window.
  // This must run before startActivityTracking() resets the persisted timestamp.
  if (isHistoryExpired()) {
    await clearZendeskChat({ reason: 'retention_expired' });
  }

  startActivityTracking();

  const handleConversationEvent = (event: ZendeskConversationEvent): void => {
    setConversationId(event.payload.conversation.id);
    touchActivity();
  };

  window.zE?.('messenger:on', 'conversationStarted', handleConversationEvent);
  window.zE?.('messenger:on', 'messageReceived', handleConversationEvent);

  // Re-check expiry on an interval as a fallback for when the tab stays in the foreground and no
  // visibilitychange fires; once the chat is cleared the listeners/interval are torn down.
  activityIntervalId = window.setInterval(async () => {
    if (!isHistoryExpired()) {
      return;
    }

    await clearZendeskChat({
      reason: 'retention_expired',
    });
  }, 60000);
};
